import {
  DefectCatalogItem,
  DefectLayerKind,
  DefectMeasurementSlug,
  DefectRulesetSlug,
  DefectRulesetSummary,
  DefectSurveyYear,
  DefectSurveyYearSlug,
  DefectVisualizationRule,
  toDefectRulesetSlug,
} from 'domain/surveyDataManager/contracts';
import { SurveyDataManagerDashboardStatsQuery } from 'domain/surveyDataManager/contracts/dashboardStats';
import {
  isDefectOverlayPresentation,
  toDefectOverlayLayerKind,
} from 'domain/surveyDataManager/defectOverlays/overlayKinds';
import type { ActiveDefectLayerId } from 'state/surveyDataManager/activeLayersSlice';
import { buildActiveLayerId } from 'state/surveyDataManager/activeLayersSlice';

import { humanizeSlug } from '../../utils/formatters';

export const DEFAULT_WIZARD_RULESET_SLUG = toDefectRulesetSlug('continuous-score');


export const CRACKMAP_WIZARD_RULESET_SLUG = toDefectRulesetSlug('default');


export function isCrackmapDefect(
  defect: DefectCatalogItem | null | undefined,
): boolean {
  return defect?.presentation === 'crackmap';
}


export function isDefectOverlayDefect(
  defect: DefectCatalogItem | null | undefined,
): boolean {
  return isDefectOverlayPresentation(defect?.presentation);
}

export function resolveCrackmapDefaultLane(
  surveyYear: DefectSurveyYear | null | undefined,
): string {
  return surveyYear?.lanes[0] ?? 'all';
}

export function resolveCrackmapSourceLayerId(
  surveyYear: DefectSurveyYear | null | undefined,
): string | null {
  const typeName = surveyYear?.sourceLayerId?.trim();
  return typeName ? typeName : null;
}

export function resolveCrackmapVectorSourceUrl(
  surveyYear: DefectSurveyYear | null | undefined,
): string | null {
  const vectorSourceUrl = surveyYear?.vectorSourceUrl?.trim();
  if (vectorSourceUrl) {
    return vectorSourceUrl;
  }
  const typeName = resolveCrackmapSourceLayerId(surveyYear);
  if (!typeName) {
    return null;
  }
  return `https://geoserver.xais.co.uk/geoserver/wfs_layers/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=${encodeURIComponent(typeName)}`;
}

export const WIZARD_STEPS = [
  { id: 1 as const, name: 'Survey type', description: 'Choose survey source' },
  { id: 2 as const, name: 'Parameter group', description: 'Choose a data group' },
  { id: 3 as const, name: 'Parameter', description: 'Choose what to display' },
  { id: 4 as const, name: 'Year', description: 'Choose survey period' },
  { id: 5 as const, name: 'Lane', description: 'Choose lane filter' },
  { id: 6 as const, name: 'Visualisation', description: 'Choose how to display it' },
];

export type WizardStepId = (typeof WIZARD_STEPS)[number]['id'];

export type WizardVisualisationClassification =
  | 'continuousScore'
  | 'percentileBands'
  | 'worstConditionOnly'
  | 'customBands';

export type WizardVisualisationOwnership =
  | 'xaisPtsRecommended'
  | 'clientPresets'
  | 'myPresets';

export interface WizardVisualisationPreset {
  readonly id: string;
  readonly slug: DefectRulesetSlug;
  readonly name: string;
  readonly description: string;
  readonly classificationType: WizardVisualisationClassification;
  readonly ownership: WizardVisualisationOwnership;
  readonly isDefault: boolean;
  readonly isStandard: boolean;
  readonly bandCount?: number;
}

export const SCANNER_VISUALISATION_PRESET_DEFINITIONS: readonly {
  readonly classificationType: WizardVisualisationClassification;
  readonly canonicalSlug: string;
  readonly name: string;
  readonly description: string;
  readonly ownership: WizardVisualisationOwnership;
  readonly isDefault: boolean;
  readonly isStandard: boolean;
  readonly matchTokens: readonly string[];
  readonly bandCount?: number;
}[] = [
  {
    classificationType: 'continuousScore',
    canonicalSlug: 'continuous-score',
    name: 'Continuous',
    description:
      'Gradient from low to high colour across the data range — no fixed engineering bands.',
    ownership: 'xaisPtsRecommended',
    isDefault: true,
    isStandard: true,
    matchTokens: ['continuous', 'score', 'raw'],
  },
  {
    classificationType: 'percentileBands',
    canonicalSlug: 'percentile-5',
    name: 'Percentile — 5 bands',
    description: 'Split the dataset into five equal percentile bands (20 / 40 / 60 / 80 / 100).',
    ownership: 'xaisPtsRecommended',
    isDefault: false,
    isStandard: false,
    matchTokens: ['percentile-5', 'percentile5', '5-band', 'five', 'percentile'],
    bandCount: 5,
  },
  {
    classificationType: 'percentileBands',
    canonicalSlug: 'percentile-3',
    name: 'Percentile — 3 bands',
    description: 'Split the dataset into three equal percentile bands.',
    ownership: 'xaisPtsRecommended',
    isDefault: false,
    isStandard: false,
    matchTokens: ['percentile-3', 'percentile3', '3-band', 'three'],
    bandCount: 3,
  },
  {
    classificationType: 'worstConditionOnly',
    canonicalSlug: 'worst-condition',
    name: 'Percentile — 1 band (worst condition)',
    description:
      'Show only the worst band — useful when stacking several parameters on the map.',
    ownership: 'xaisPtsRecommended',
    isDefault: false,
    isStandard: false,
    matchTokens: ['worst-condition', 'worst_condition', 'worst', 'percentile-1'],
    bandCount: 1,
  },
  {
    classificationType: 'customBands',
    canonicalSlug: 'custom-bands',
    name: 'Client standard',
    description:
      'Set your own thresholds and colours for this layer (scheme-specific cut-offs).',
    ownership: 'clientPresets',
    isDefault: false,
    isStandard: false,
    matchTokens: ['custom', 'authority', 'local', 'extended', 'client'],
  },
];
export interface WizardSelections {
  readonly surveyType: string | null;
  readonly defectGroup: string | null;
  readonly defect: DefectCatalogItem | null;
  readonly measurement: DefectVisualizationRule | null;
  readonly surveyYearSlug: DefectSurveyYearSlug | null;
  readonly lanes: readonly string[];
  readonly visualisationPreset: WizardVisualisationPreset | null;
  readonly colourOverride: {
    readonly colorLow: string;
    readonly colorHigh: string;
  } | null;
  readonly displayName: string | null;
}

export function deriveSurveyTypes(
  defects: readonly DefectCatalogItem[],
): readonly string[] {
  const types = new Set<string>();
  for (const defect of defects) {
    if (defect.surveyType) {
      types.add(defect.surveyType);
    }
  }
  return Array.from(types).sort((a, b) => a.localeCompare(b));
}

export function deriveDefectsForSurveyType(
  defects: readonly DefectCatalogItem[],
  surveyType: string | null,
): readonly DefectCatalogItem[] {
  if (!surveyType) {
    return [];
  }

  const normalizedType = surveyType.toLowerCase();
  return defects
    .filter(
      (defect) => defect.surveyType?.trim().toLowerCase() === normalizedType,
    )
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

export function deriveDefectGroupsForSurveyType(
  defects: readonly DefectCatalogItem[],
  surveyType: string | null,
): readonly string[] {
  const groups = new Set<string>();
  for (const defect of deriveDefectsForSurveyType(defects, surveyType)) {
    const group = defect.type.trim();
    if (group) {
      groups.add(group);
    }
  }
  return Array.from(groups).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
}

export interface WizardParameterOption {
  readonly defect: DefectCatalogItem;
  readonly measurement: DefectVisualizationRule;
}

export function deriveParameterOptionsForDefectGroup(
  defects: readonly DefectCatalogItem[],
  surveyType: string | null,
  defectGroup: string | null,
): readonly WizardParameterOption[] {
  if (!defectGroup) {
    return [];
  }

  const normalizedGroup = defectGroup.trim().toLowerCase();
  return deriveDefectsForSurveyType(defects, surveyType)
    .filter((defect) => defect.type.trim().toLowerCase() === normalizedGroup)
    .flatMap((defect) =>
      defect.measurements.map((measurement) => ({ defect, measurement })),
    )
    .sort((left, right) =>
      left.measurement.name.localeCompare(right.measurement.name, undefined, {
        sensitivity: 'base',
      }),
    );
}

export interface WizardCatalogSearchMatch {
  readonly surveyType: string;
  readonly defect: DefectCatalogItem;
  readonly label: string;
}

function tokenMatchesSurveyType(token: string, surveyType: string): boolean {
  const normalized = surveyType.trim().toLowerCase();
  const humanized = humanizeSlug(surveyType).toLowerCase();
  return normalized.includes(token) || humanized.includes(token);
}

function tokenMatchesDefect(token: string, defect: DefectCatalogItem): boolean {
  const name = defect.name.toLowerCase();
  const code = defect.code.toLowerCase();
  const slug = defect.slug.toLowerCase();
  const group = defect.type.toLowerCase();
  return (
    name.includes(token) ||
    code.includes(token) ||
    slug.includes(token) ||
    group.includes(token)
  );
}

/**
 * Resolves free-text search (e.g. "Scanner cracking") to survey type + defect pairs.
 * Every token must match the survey type and/or defect; multi-token queries must hit both.
 */
export function searchWizardCatalog(
  defects: readonly DefectCatalogItem[],
  query: string,
): readonly WizardCatalogSearchMatch[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return [];
  }

  const tokens = normalized.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) {
    return [];
  }

  const matches: WizardCatalogSearchMatch[] = [];

  for (const defect of defects) {
    const surveyType = defect.surveyType?.trim() ?? '';
    if (!surveyType) {
      continue;
    }

    const everyTokenMatchesRow = tokens.every(
      (token) =>
        tokenMatchesSurveyType(token, surveyType) || tokenMatchesDefect(token, defect),
    );

    if (!everyTokenMatchesRow) {
      continue;
    }

    if (tokens.length > 1) {
      const hitsSurveyType = tokens.some((token) =>
        tokenMatchesSurveyType(token, surveyType),
      );
      const hitsDefect = tokens.some((token) => tokenMatchesDefect(token, defect));
      if (!hitsSurveyType || !hitsDefect) {
        continue;
      }
    }

    matches.push({
      surveyType,
      defect,
      label: `${humanizeSlug(surveyType)} · ${defect.type} · ${defect.name}`,
    });
  }

  return matches.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

export function countDefectSurveyYears(defect: DefectCatalogItem): number {
  const slugs = new Set<string>();
  for (const measurement of defect.measurements) {
    for (const year of measurement.surveyYears) {
      slugs.add(year.slug);
    }
  }
  return slugs.size;
}

export function buildWizardActiveLayerIds(
  activeLayers: readonly { readonly id: ActiveDefectLayerId }[],
): ReadonlySet<ActiveDefectLayerId> {
  return new Set(activeLayers.map((layer) => layer.id));
}

export function isWizardLayerIdActive(
  activeLayerIds: ReadonlySet<ActiveDefectLayerId>,
  input: {
    readonly defectSlug: DefectCatalogItem['slug'];
    readonly measurementSlug: DefectMeasurementSlug;
    readonly surveyYearSlug: DefectSurveyYearSlug;
    readonly lane: string;
    readonly rulesetSlug?: DefectRulesetSlug;
  },
): boolean {
  return activeLayerIds.has(
    buildActiveLayerId({
      defectSlug: input.defectSlug,
      measurementSlug: input.measurementSlug,
      surveyYearSlug: input.surveyYearSlug,
      lane: input.lane,
      rulesetSlug: input.rulesetSlug ?? DEFAULT_WIZARD_RULESET_SLUG,
    }),
  );
}

export function isWizardVisualisationPresetActive(
  activeLayerIds: ReadonlySet<ActiveDefectLayerId>,
  selections: WizardSelections,
  preset: WizardVisualisationPreset,
): boolean {
  const addableLanes = resolveAddableLanesForPreset(
    activeLayerIds,
    selections,
    preset,
  );
  return selections.lanes.length > 0 && addableLanes.length === 0;
}

export function resolveAddableLanesForPreset(
  activeLayerIds: ReadonlySet<ActiveDefectLayerId>,
  selections: WizardSelections,
  preset: WizardVisualisationPreset,
): readonly string[] {
  if (
    !selections.defect ||
    !selections.measurement ||
    !selections.surveyYearSlug ||
    selections.lanes.length === 0
  ) {
    return [];
  }

  return selections.lanes.filter(
    (lane) =>
      !isWizardLayerIdActive(activeLayerIds, {
        defectSlug: selections.defect!.slug,
        measurementSlug: selections.measurement!.slug,
        surveyYearSlug: selections.surveyYearSlug!,
        lane,
        rulesetSlug: preset.slug,
      }),
  );
}

export function toggleWizardLaneSelection(
  currentLanes: readonly string[],
  lane: string,
): readonly string[] {
  if (currentLanes.includes(lane)) {
    return currentLanes.filter((entry) => entry !== lane);
  }
  return [...currentLanes, lane].sort((left, right) =>
    left.localeCompare(right, undefined, { sensitivity: 'base' }),
  );
}

export function formatWizardLanesLabel(
  selectedLanes: readonly string[],
  availableLanes: readonly string[] = selectedLanes,
): string {
  if (selectedLanes.length === 0) {
    return '';
  }
  if (
    availableLanes.length > 0 &&
    selectedLanes.length === availableLanes.length &&
    availableLanes.every((lane) => selectedLanes.includes(lane))
  ) {
    return 'All Lanes';
  }
  return selectedLanes.map((lane) => humanizeSlug(lane)).join(', ');
}

export function visualisationOwnershipLabel(
  ownership: WizardVisualisationOwnership,
): string {
  switch (ownership) {
    case 'xaisPtsRecommended':
      return 'XAIS-PTS Recommended';
    case 'clientPresets':
      return 'Client Presets';
    case 'myPresets':
      return 'My Presets';
    default: {
      const unreachable: never = ownership;
      return unreachable;
    }
  }
}

export function visualisationClassificationLabel(
  classificationType: WizardVisualisationClassification,
): string {
  switch (classificationType) {
    case 'continuousScore':
      return 'Continuous';
    case 'customBands':
      return 'Client standard';
    case 'percentileBands':
      return 'Percentile bands';
    case 'worstConditionOnly':
      return 'Worst condition';
    default: {
      const unreachable: never = classificationType;
      return unreachable;
    }
  }
}

function pickRulesetForDefinition(
  rulesets: readonly DefectRulesetSummary[],
  definition: (typeof SCANNER_VISUALISATION_PRESET_DEFINITIONS)[number],
  claimedSlugs: Set<string>,
): DefectRulesetSummary | null {
  const byToken = rulesets.find((ruleset) => {
    const slug = ruleset.slug.trim().toLowerCase();
    if (claimedSlugs.has(slug)) {
      return false;
    }
    return definition.matchTokens.some(
      (token) => slug.includes(token) || ruleset.name.toLowerCase().includes(token),
    );
  });
  if (byToken) {
    return byToken;
  }

  if (definition.isDefault) {
    return (
      rulesets.find(
        (ruleset) => ruleset.default && !claimedSlugs.has(ruleset.slug.trim().toLowerCase()),
      ) ?? null
    );
  }

  return null;
}

export function buildWizardVisualisationPresets(
  rulesets: readonly DefectRulesetSummary[],
): readonly WizardVisualisationPreset[] {
  const claimedSlugs = new Set<string>();

  return SCANNER_VISUALISATION_PRESET_DEFINITIONS.map((definition) => {
    const matched = pickRulesetForDefinition(rulesets, definition, claimedSlugs);
    if (matched) {
      claimedSlugs.add(matched.slug.trim().toLowerCase());
    }

    const slug = matched?.slug ?? toDefectRulesetSlug(definition.canonicalSlug);
    return {
      id: matched?.id ?? definition.canonicalSlug,
      slug,
      name: definition.name,
      description: definition.description,
      classificationType: definition.classificationType,
      ownership: definition.ownership,
      isDefault: definition.isDefault,
      isStandard: definition.isStandard,
      ...(definition.bandCount != null ? { bandCount: definition.bandCount } : {}),
    };
  });
}

export function resolveDefaultVisualisationPreset(
  presets: readonly WizardVisualisationPreset[],
): WizardVisualisationPreset | null {
  return presets.find((preset) => preset.isDefault) ?? presets[0] ?? null;
}

export function isWizardMeasurementFullyActive(
  activeLayerIds: ReadonlySet<ActiveDefectLayerId>,
  defect: DefectCatalogItem,
  measurement: DefectVisualizationRule,
): boolean {
  let hasAnyOption = false;

  for (const year of measurement.surveyYears) {
    for (const lane of year.lanes) {
      hasAnyOption = true;
      if (
        !isWizardLayerIdActive(activeLayerIds, {
          defectSlug: defect.slug,
          measurementSlug: measurement.slug,
          surveyYearSlug: year.slug,
          lane,
        })
      ) {
        return false;
      }
    }
  }

  return hasAnyOption;
}

export function isWizardDefectFullyActive(
  activeLayerIds: ReadonlySet<ActiveDefectLayerId>,
  defect: DefectCatalogItem,
): boolean {
  if (defect.measurements.length === 0) {
    return false;
  }

  return defect.measurements.every((measurement) =>
    isWizardMeasurementFullyActive(activeLayerIds, defect, measurement),
  );
}

export function isWizardDefectGroupFullyActive(
  activeLayerIds: ReadonlySet<ActiveDefectLayerId>,
  defects: readonly DefectCatalogItem[],
  surveyType: string | null,
  defectGroup: string,
): boolean {
  const options = deriveParameterOptionsForDefectGroup(
    defects,
    surveyType,
    defectGroup,
  );
  return (
    options.length > 0 &&
    options.every(({ defect, measurement }) =>
      isWizardMeasurementFullyActive(activeLayerIds, defect, measurement),
    )
  );
}

export function isWizardSurveyYearFullyActive(
  activeLayerIds: ReadonlySet<ActiveDefectLayerId>,
  defect: DefectCatalogItem,
  measurement: DefectVisualizationRule,
  surveyYear: DefectSurveyYear,
): boolean {
  if (surveyYear.lanes.length === 0) {
    return false;
  }

  return surveyYear.lanes.every((lane) =>
    isWizardLayerIdActive(activeLayerIds, {
      defectSlug: defect.slug,
      measurementSlug: measurement.slug,
      surveyYearSlug: surveyYear.slug,
      lane,
    }),
  );
}

export function isWizardLaneActive(
  activeLayerIds: ReadonlySet<ActiveDefectLayerId>,
  defect: DefectCatalogItem,
  measurement: DefectVisualizationRule,
  surveyYearSlug: DefectSurveyYearSlug,
  lane: string,
): boolean {
  return isWizardLayerIdActive(activeLayerIds, {
    defectSlug: defect.slug,
    measurementSlug: measurement.slug,
    surveyYearSlug,
    lane,
  });
}

export function isWizardCompletionBlocked(
  selections: WizardSelections,
  activeLayerIds: ReadonlySet<ActiveDefectLayerId>,
): boolean {
  if (!selections.visualisationPreset) {
    return true;
  }

  return (
    resolveAddableLanesForPreset(
      activeLayerIds,
      selections,
      selections.visualisationPreset,
    ).length === 0
  );
}

export function canProceedToNextStep(
  step: WizardStepId,
  selections: WizardSelections,
  activeLayerIds: ReadonlySet<ActiveDefectLayerId> = new Set(),
): boolean {
  const isOverlay = isDefectOverlayDefect(selections.defect);

  switch (step) {
    case 1:
      return selections.surveyType !== null;
    case 2:
      return selections.defectGroup !== null;
    case 3:
      return selections.defect !== null && selections.measurement !== null;
    case 4:
      if (!selections.surveyYearSlug) {
        return false;
      }
      if (isOverlay) {
        const surveyYear = selections.measurement?.surveyYears.find(
          (year) => year.slug === selections.surveyYearSlug,
        );
        if (!resolveCrackmapVectorSourceUrl(surveyYear)) {
          return false;
        }
        const lane = resolveCrackmapDefaultLane(surveyYear);
        const layerId = buildActiveLayerId({
          defectSlug: selections.defect!.slug,
          measurementSlug: selections.measurement!.slug,
          surveyYearSlug: selections.surveyYearSlug,
          rulesetSlug: CRACKMAP_WIZARD_RULESET_SLUG,
          lane,
        });
        return !activeLayerIds.has(layerId);
      }
      return true;
    case 5:
      return selections.lanes.length > 0;
    case 6:
      return (
        selections.visualisationPreset !== null &&
        !isWizardCompletionBlocked(selections, activeLayerIds)
      );
    default: {
      const unreachable: never = step;
      return unreachable;
    }
  }
}

export function isCrackmapWizardCompleteStep(
  step: WizardStepId,
  selections: WizardSelections,
): boolean {
  return isDefectOverlayDefect(selections.defect) && step === 4;
}

export interface WizardBreadcrumbItem {
  readonly stepId: WizardStepId;
  readonly stepName: string;
  readonly selectionLabel: string | null;
  readonly isComplete: boolean;
  readonly isCurrent: boolean;
  readonly canNavigate: boolean;
}

export function buildWizardBreadcrumbs(
  currentStep: WizardStepId,
  selections: WizardSelections,
  surveyYearDisplay: string,
  availableLanes: readonly string[] = selections.lanes,
): readonly WizardBreadcrumbItem[] {
  const selectionValues: readonly (string | null)[] = [
    selections.surveyType ? humanizeSlug(selections.surveyType) : null,
    selections.defectGroup,
    selections.measurement?.name ?? null,
    surveyYearDisplay.trim() ? surveyYearDisplay : null,
    selections.lanes.length > 0
      ? formatWizardLanesLabel(selections.lanes, availableLanes)
      : null,
    selections.visualisationPreset?.name ?? null,
  ];

  return WIZARD_STEPS.map((step, index) => {
    const selectionLabel = selectionValues[index] ?? null;
    const isComplete = selectionLabel !== null;
    const isCurrent = currentStep === step.id;
    const canNavigate = isComplete && step.id < currentStep;

    return {
      stepId: step.id,
      stepName: step.name,
      selectionLabel,
      isComplete,
      isCurrent,
      canNavigate,
    };
  });
}

export function buildLayerDisplayName(
  surveyType: string,
  measurement: DefectVisualizationRule,
  surveyYearLabel: string,
  laneLabel: string,
): string {
  return `${humanizeSlug(surveyType)} – ${measurement.name} – ${surveyYearLabel} – ${laneLabel}`;
}

export function buildSuggestedLayerDisplayName(
  selections: WizardSelections,
  availableLanes: readonly string[] = selections.lanes,
): string | null {
  const { surveyType, measurement, surveyYearSlug, lanes } = selections;
  if (!surveyType || !measurement || !surveyYearSlug || lanes.length === 0) {
    return null;
  }

  const surveyYear = measurement.surveyYears.find(
    (year) => year.slug === surveyYearSlug,
  );
  const surveyYearLabel = surveyYear?.surveyYear ?? String(surveyYearSlug);
  return buildLayerDisplayName(
    surveyType,
    measurement,
    surveyYearLabel,
    formatWizardLanesLabel(lanes, availableLanes),
  );
}

export interface CompletedWizardLayer {
  readonly defectSlug: DefectCatalogItem['slug'];
  readonly measurementSlug: DefectVisualizationRule['slug'];
  readonly surveyYearSlug: DefectSurveyYearSlug;
  readonly rulesetSlug: DefectRulesetSlug;
  readonly lane: string;
  readonly displayName: string;
  readonly layerKind: DefectLayerKind;
  readonly sourceLayerId?: string;
  readonly vectorSourceUrl?: string;
  readonly colourOverride?: {
  readonly colorLow: string;
  readonly colorHigh: string;
  } | null;
}

export function buildCompletedLayers(
  selections: WizardSelections,
  activeLayerIds: ReadonlySet<ActiveDefectLayerId> = new Set(),
): readonly CompletedWizardLayer[] | null {
  const {
    surveyType,
    defect,
    measurement,
    surveyYearSlug,
    colourOverride,
    displayName,
  } = selections;
  if (!surveyType || !defect || !measurement || !surveyYearSlug) {
    return null;
  }

  const surveyYear = measurement.surveyYears.find(
    (year) => year.slug === surveyYearSlug,
  );
  const surveyYearLabel = surveyYear?.surveyYear ?? String(surveyYearSlug);
  const customName = displayName?.trim() ?? '';
  const isOverlay = isDefectOverlayDefect(defect);

  if (isOverlay) {
    const lane = resolveCrackmapDefaultLane(surveyYear);
    const layerId = buildActiveLayerId({
      defectSlug: defect.slug,
      measurementSlug: measurement.slug,
      surveyYearSlug,
      rulesetSlug: CRACKMAP_WIZARD_RULESET_SLUG,
      lane,
    });
    if (activeLayerIds.has(layerId)) {
      return null;
    }
    const sourceLayerId = resolveCrackmapSourceLayerId(surveyYear);
    const vectorSourceUrl = resolveCrackmapVectorSourceUrl(surveyYear);
    if (!vectorSourceUrl) {
      return null;
    }
    return [
      {
        defectSlug: defect.slug,
        measurementSlug: measurement.slug,
        surveyYearSlug,
        rulesetSlug: CRACKMAP_WIZARD_RULESET_SLUG,
        lane,
        displayName:
          customName ||
          buildLayerDisplayName(
            surveyType,
            measurement,
            surveyYearLabel,
            humanizeSlug(lane),
          ),
        layerKind: toDefectOverlayLayerKind(defect.presentation),
        ...(sourceLayerId ? { sourceLayerId } : {}),
        vectorSourceUrl,
        colourOverride: null,
      },
    ];
  }

  const visualisationPreset = selections.visualisationPreset;
  if (!visualisationPreset || selections.lanes.length === 0) {
    return null;
  }

  const addableLanes = resolveAddableLanesForPreset(
    activeLayerIds,
    selections,
    visualisationPreset,
  );
  if (addableLanes.length === 0) {
    return null;
  }

  return addableLanes.map((lane) => ({
    defectSlug: defect.slug,
    measurementSlug: measurement.slug,
    surveyYearSlug,
    rulesetSlug: visualisationPreset.slug,
    lane,
    displayName:
      addableLanes.length === 1 && customName
        ? customName
        : buildLayerDisplayName(
            surveyType,
            measurement,
            surveyYearLabel,
            humanizeSlug(lane),
          ),
    layerKind: 'map-vector' as const,
    colourOverride: colourOverride ?? null,
  }));
}

export function buildCompletedLayer(
  selections: WizardSelections,
  activeLayerIds: ReadonlySet<ActiveDefectLayerId> = new Set(),
): CompletedWizardLayer | null {
  const layers = buildCompletedLayers(selections, activeLayerIds);
  return layers?.[0] ?? null;
}

export function buildWizardStatsQuery(
  selections: WizardSelections,
  surveyYearLabel: string,
): SurveyDataManagerDashboardStatsQuery {
  const query: {
    surveyType?: string;
    surveyYear?: string;
    lane?: string;
  } = {};

  if (selections.surveyType) {
    query.surveyType = selections.surveyType;
  }
  const trimmedSurveyYear = surveyYearLabel.trim();
  if (trimmedSurveyYear) {
    query.surveyYear = trimmedSurveyYear;
  }
  if (selections.lanes.length === 1) {
    query.lane = selections.lanes[0];
  }

  return query;
}
